import {
  InputWysiwyg,
  Form,
  useFormConfig,
  Button,
  FormField,
  FormGrid,
  FormRow,
} from "@endya-conformite/ui-kit";
import AddIcon from "@endya-conformite/ui-kit/src/assets/icons/plus-circle.svg?react";
import TrashIcon from "@endya-conformite/ui-kit/src/assets/icons/trash.svg?react";
import { zod } from "@endya-conformite/ui-kit/src/lya/exports";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import style from "./CQRemediationForm.module.scss";

const HTML_TAG_REGEX = /<[^>]*>/g;

const schema = zod.object({
  files: zod.array(
    zod.object({
      idFile: zod.string().optional(),
      file: zod.instanceof(File).optional(),
      pathname: zod.string(),
      filename: zod.string(),
    })
  ),
  description: zod.string(),
});

const normalizeDescription = (html: string) =>
  html.replace(HTML_TAG_REGEX, "").trim();

export type CQRemediationFormValues = zod.infer<typeof schema>;
type CQRemediationValueFile = CQRemediationFormValues["files"][0];

type CQRemediationFormProps = {
  onSubmit: (values: CQRemediationFormValues) => unknown;
  formId: string;
  onDirtyChange?: (dirty: { description: boolean; files: boolean }) => void;
  onValuesChange?: (values: CQRemediationFormValues) => void;
  initialValues?: CQRemediationFormValues;
};

export function CQRemediationForm({
  onSubmit,
  formId,
  onDirtyChange,
  onValuesChange,
  initialValues,
}: CQRemediationFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formConfig = useFormConfig<typeof schema, CQRemediationFormValues>({
    schema,
    defaultValues: initialValues ?? { files: [], description: "" },
  });

  const currentValues = formConfig.watch();
  const initialDescription = normalizeDescription(
    initialValues?.description ?? ""
  );
  const initialFiles = initialValues?.files ?? [];
  const initialFileKeys = new Set(
    initialFiles.map((file) => file.idFile ?? file.pathname)
  );
  const descriptionDirty =
    normalizeDescription(currentValues.description) !== initialDescription;
  const filesDirty =
    currentValues.files.length !== initialFiles.length ||
    currentValues.files.some(
      (file) => !initialFileKeys.has(file.idFile ?? file.pathname)
    );

  useEffect(() => {
    onDirtyChange?.({ description: descriptionDirty, files: filesDirty });
  }, [descriptionDirty, filesDirty, onDirtyChange]);

  useEffect(() => {
    onValuesChange?.(currentValues);
  }, [currentValues.description, currentValues.files, onValuesChange]);

  const handleAddFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files.length === 0) return;
      formConfig.setValue("files", [
        ...formConfig.getValues().files,
        ...Array.from(files).map((f) => ({
          file: f,
          filename: f.name,
          pathname: Math.random().toString(10),
        })),
      ]);
      event.target.value = "";
    },
    [formConfig]
  );

  const handleRemoveFile = useCallback(
    (file: CQRemediationValueFile) => () => {
      formConfig.setValue(
        "files",
        formConfig.getValues("files").filter((f) => f !== file)
      );
    },
    [formConfig]
  );

  const files = formConfig.watch("files");

  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const droppedFiles = e.dataTransfer.files;
      if (!droppedFiles || droppedFiles.length === 0) return;
      formConfig.setValue("files", [
        ...formConfig.getValues().files,
        ...Array.from(droppedFiles).map((f) => ({
          file: f,
          filename: f.name,
          pathname: Math.random().toString(10),
        })),
      ]);
    },
    [formConfig]
  );

  return (
    <Form id={formId} formConfig={formConfig} onSubmit={onSubmit}>
      <FormGrid disableHorizontalGutter>
        <FormRow>
          <FormField>
            <InputWysiwyg<CQRemediationFormValues>
              name="description"
              placeholder="Écrire un commentaire de remédiation..."
              resize="vertical"
            />
          </FormField>
        </FormRow>
      </FormGrid>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={classNames(style.attachmentZone, {
          [style.isDragOver]: isDragOver,
        })}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className={style.fileInputHidden}
          onChange={handleAddFile}
        />
        {files.length > 0 && (
          <div className={style.files}>
            {files.map((file) => (
              <div key={file.pathname} className={style.file}>
                <p>{file.filename}</p>
                <Button
                  variant="text"
                  icon={<TrashIcon />}
                  onClick={handleRemoveFile(file)}
                />
              </div>
            ))}
          </div>
        )}
        <Button
          variant="text"
          icon={<AddIcon />}
          onClick={() => fileInputRef.current?.click()}
        >
          Ajouter une pièce jointe
        </Button>
      </div>
    </Form>
  );
}
